import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea, CustomDatePicker } from "@components/Forms";
import { Breadcrumb, CustomSelect, FileUploader } from "@components/UI";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

export default function AnnouncementAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { postClassList, postTypeList, fetchSearchInfo } = useSearchInfo();
  const { uploadFilesWithCustomCallback } = useFileGroupAPI(0);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    postClassCd: "",
    postTypeCd: "",
    postStartDt: "",
    postEndDt: "",
    mainPopupFlag: "Y",
    topPostingFlag: "Y",
  });

  // Date range state for DatePicker
  const [dateRange, setDateRange] = useState([null, null]);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialMount = useRef(true);

  // Helper function to create CustomSelect value object
  const createSelectValue = (currentValue, optionsList) => {
    if (!currentValue || !optionsList) return null;
    const option = optionsList.find((o) => o.value === currentValue);
    return option ? { value: currentValue, label: t(option.label) } : null;
  };

  useEffect(() => {
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t("bulletin.title-required");
    }

    if (!formData.content.trim()) {
      newErrors.content = t("bulletin.content-required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addAnnouncement = useMutationData(
    queryKeys.addAnnouncement,
    {
      method: "POST",
    },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.BULLETINS, response, response.bulletinSeq);
        showSuccessToast(t("bulletin.announcement-add-success"));
        // Handle file upload if files are selected
        if (selectedFiles.length > 0) {
          const formDataForUpload = new FormData();
          formDataForUpload.append("targetSeq", response.bulletinSeq);
          formDataForUpload.append("targetTypeCd", "BULLETIN");
          formDataForUpload.append("fileGroupNo", response.fileGroupNo ? response.fileGroupNo : 0);

          const uploadFilesData = selectedFiles.reduce((acc, file, index) => {
            if (file.file instanceof File) {
              acc[`file_${index}`] = file.file;
            }
            return acc;
          }, {});

          Object.entries(uploadFilesData).forEach(([key, file]) => {
            formDataForUpload.append(key, file, file.name);
          });

          uploadFilesWithCustomCallback.mutate(formDataForUpload, {
            onSuccess: () => {
              showSuccessToast(t("common.files-upload-success"));
              navigate(`/announcements/${response.bulletinSeq}`);
            },
            onError: (error) => {
              console.error("Error during upload data:", error);
              showErrorToast(t("common.files-upload-error"));
              navigate(`/announcements/${response.bulletinSeq}`);
            },
          });
        } else {
          navigate(`/announcements/${response.bulletinSeq}`);
        }
      },
      onError: (error) => {
        console.error("Error during create data:", error);
        showErrorToast(t("bulletin.announcement-add-error"));
        setIsSubmitting(false);
      },
    },
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    await addAnnouncement.mutateAsync(formData);
  };

  const handleCancel = () => {
    navigate("/announcements");
  };

  const isFormValid = formData.title.trim() && formData.content.trim();

  return (
    <div data-testid="announcement-add-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="announcement-add-header">
        <div className="flex items-center space-x-4">
          <Breadcrumb
            data-testid="announcement-add-breadcrumb"
            items={[
              { label: t("bulletin.announcement-list-title"), href: "/announcements" },
              { label: t("bulletin.add-new-announcement") },
            ]}
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <h1
            className={`${colors.textPrimary} text-2xl font-bold`}
            data-testid="announcement-add-page-title"
          >
            {uppercase(t("bulletin.add-new-announcement"))}
          </h1>

          <div className="flex items-center space-x-3" data-testid="announcement-add-header-actions">
            <OutlineButton
              data-testid="announcement-add-cancel-button"
              onClick={handleCancel}
              className={`rounded-lg border px-4 py-1 font-semibold focus:outline-none ${colors.textSecondary} ${colors.borderHoverPrimary} ${colors.textHoverActive}`}
            >
              {t("common.cancel-button")}
            </OutlineButton>

            <PrimaryButton data-testid="announcement-add-save-button" onClick={handleSubmit} disabled={!isFormValid || addAnnouncement.isPending}>
              {addAnnouncement.isPending ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`}></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="announcement-add-form">
        <div className={`mx-auto max-w-[56rem]`}>
          <div>
            {/* Announcement Information Section */}
            <div className="mb-6" data-testid="announcement-add-information-section">
              <h2
                className={`${colors.textPrimary} text-xl font-semibold pt-2 pb-6`}
                data-testid="announcement-add-information-title"
              >
                {t("bulletin.announcement-information")}
              </h2>

              <div className="grid grid-cols-3 gap-6">
                {/* Title */}
                <div className="col-span-3">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.title")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomInput
                    data-testid="announcement-add-title-input"
                    type="text"
                    placeholder={t("bulletin.enter-title")}
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    title={formData.title}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    error={errors.title}
                    maxLength={CHARACTER_LENGTH.COMMENT}
                  />
                </div>

                {/* Content */}
                <div className="col-span-3">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.content")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid="announcement-add-content-textarea"
                    rows={6}
                    placeholder={t("bulletin.enter-content")}
                    value={formData.content}
                    onChange={(e) => handleInputChange("content", e.target.value)}
                    title={formData.content}
                    className={`w-full rounded-lg border text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                    error={errors.content}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="announcement-add-content-textarea-char-count">{(formData.content || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>

                {/* Post Class */}
                <div className="md:col-span-1">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.post-class")}
                  </label>
                  <CustomSelect
                    data-testid="announcement-add-post-class-select"
                    options={postClassList?.map((option) => ({ value: option.value, label: t(option.label) })) || []}
                    value={createSelectValue(formData.postClassCd, postClassList)}
                    onChange={(opt) => handleInputChange("postClassCd", opt?.value || "")}
                    placeholder={t("bulletin.post-class-options-placeholder")}
                    size="md"
                    menuPlacement="auto"
                    clearable={true}
                  />
                </div>

                {/* Post Date Range */}
                <div className="md:col-span-1">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.post-date-range")}
                  </label>
                  <CustomDatePicker
                    data-testid="announcement-add-post-date-range-picker"
                    rangeSeparator=' → '
                    selected={dateRange[0]}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setDateRange([start, end]);
                      if (start) {
                        const startDateStr = start.toLocaleDateString("en-CA");
                        setFormData((prev) => ({
                          ...prev,
                          postStartDt: startDateStr,
                        }));
                      }
                      if (end) {
                        const endDateStr = end.toLocaleDateString("en-CA");
                        setFormData((prev) => ({
                          ...prev,
                          postEndDt: endDateStr,
                        }));
                      }
                    }}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    selectsRange
                    placeholderText={t("bulletin.select-date-range")}
                    className={`h-10 w-full appearance-none rounded-lg border p-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                    filterDate={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
                  />
                </div>

                {/* Main Popup */}
                <div className="md:col-span-1">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("bulletin.main-popup")}
                  </label>
                  <div className="flex items-center pt-2">
                    <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                      <input
                        data-testid="announcement-add-main-popup-checkbox"
                        type="checkbox"
                        className="sr-only peer"
                        checked={formData.mainPopupFlag === "Y"}
                        onChange={(e) => handleInputChange("mainPopupFlag", e.target.checked ? "Y" : "N")}
                      />
                      <div
                        className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${formData.mainPopupFlag === "Y" ? "border-none" : colors.borderPrimary} ${formData.mainPopupFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                      ></div>
                      <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                        {formData.mainPopupFlag === "Y" ? t("common.yes") : t("common.no")}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className="mb-6" data-testid="announcement-add-attachments-section">
              <h2
                className={`${colors.textPrimary} mb-2 text-lg font-semibold`}
                data-testid="announcement-add-attachments-title"
              >
                {uppercase(t("bulletin.attachment"))}
              </h2>
              <FileUploader
                data-testid="announcement-add-file-uploader"
                onFilesChange={(files) => setSelectedFiles(files)}
                selectedFiles={selectedFiles}
                multiple={true}
              />
            </div>
          </div>
        </div>
      </div>
      <FullScreenDotLoader
        loading={uploadFilesWithCustomCallback.isPending}
        text={
          uploadFilesWithCustomCallback.isPending
            ? t("bulletin.uploading-announcement") || "Uploading announcement file..."
            : t("bulletin.upload-announcement-file") || "Uploading announcement file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
